import { useEffect } from 'react';
import {
  isTrackReference,
  useConnectionQualityIndicator,
  useLocalParticipant,
  useRemoteParticipants,
  useRoomContext,
  useTracks,
} from '@livekit/components-react';
import {
  Participant,
  RemoteParticipant,
  RoomEvent,
  Track,
  TrackPublication,
} from 'livekit-client';

import { activeDeviceLabel } from '../deviceOptions';
import { useLocalDevices } from './useLocalDevices';
import { CallParticipants, RemoteCallParticipant } from '../types';

export const findPatientParticipant = (
  participants: RemoteCallParticipant[]
): RemoteCallParticipant | undefined =>
  participants.find((participant) => participant.role === 'patient');

export const isOff = (
  participant: Participant,
  source: Track.Source
): boolean => participant.getTrackPublication(source)?.isMuted ?? false;

// Switching device restarts the local track in place, and LiveKit sends no
// participant event for that, so the microphone name would keep the old label.
// Reading the devices again also renders the call with the new name.
const useMicrophoneName = (
  microphoneTrack?: TrackPublication
): string | undefined => {
  const room = useRoomContext();
  const { state: devices, refresh } = useLocalDevices();
  useEffect(() => {
    room.on(RoomEvent.ActiveDeviceChanged, refresh);
    return () => {
      room.off(RoomEvent.ActiveDeviceChanged, refresh);
    };
  }, [room, refresh]);

  return activeDeviceLabel({
    devices: devices.kind === 'ready' ? devices.devices.audioinput : [],
    activeDeviceId: room.getActiveDevice('audioinput'),
    // The browser only names the device once access to it has been allowed.
    trackLabel: microphoneTrack?.track?.mediaStreamTrack.label,
  });
};

export const useCallParticipants = (): CallParticipants => {
  const {
    isCameraEnabled,
    isMicrophoneEnabled,
    localParticipant,
    microphoneTrack,
  } = useLocalParticipant();
  const microphoneName = useMicrophoneName(microphoneTrack);
  const { quality: localQuality } = useConnectionQualityIndicator({
    participant: localParticipant,
  });
  // Re-renders on every remote participant's quality and mute changes, so the
  // values read off each participant below stay current.
  const remoteParticipants = useRemoteParticipants();

  const cameras = new Map(
    useTracks([Track.Source.Camera])
      .filter(isTrackReference)
      .map((track) => [track.participant.identity, track])
  );
  const cameraOf = (participant: Participant) =>
    cameras.get(participant.identity);

  const toCallParticipant = (
    participant: RemoteParticipant
  ): RemoteCallParticipant => ({
    participant,
    role: participant.attributes?.role,
    quality: participant.connectionQuality,
    cameraOff: isOff(participant, Track.Source.Camera),
    micOff: isOff(participant, Track.Source.Microphone),
    camera: cameraOf(participant),
  });

  return {
    self: {
      participant: localParticipant,
      quality: localQuality,
      cameraOff: !isCameraEnabled,
      micOff: !isMicrophoneEnabled,
      microphoneName,
      camera: cameraOf(localParticipant),
    },
    participants: remoteParticipants.map(toCallParticipant),
  };
};
